import {
  CASE_FACTORS,
  FACTOR_STATUS_FIELD,
  getActiveCaseFactors,
  resolveClaimFactorValues
} from './pricingService';
import { INSTANCE_TRACKED_FACTOR_KEYS } from '../utils/factorInstances';

const parseJson = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

const yn = (v) => String(v || '').trim();

/**
 * Real PF-claim obstacle options by activity type
 */
const OBSTACLE_BY_FACTOR = {
  transfers: [
    'Previous employer not approving transfer',
    'Employer DSC / digital sign pending',
    'Previous Member ID / UAN mismatch',
    'Name / DOB mismatch on old account',
    'Aadhaar not seeded on previous UAN',
    'Form-13 rejected / returned by EPFO',
    'Transfer claim pending with EPFO',
    'Multiple UANs not merged / linked',
    'Company closed / no DSC available',
    'EPFO technical / site error'
  ],
  jd_issues: [
    'Employer not signing Joint Declaration',
    'Name mismatch (Aadhaar vs PF)',
    'Date of birth mismatch',
    'Father / spouse name mismatch',
    'Mobile / email not linked to UAN',
    'JD returned by EPFO',
    'Member Aadhaar OTP / biometrics issue',
    'EPFO technical / site error'
  ],
  exit_date: [
    'Employer not updating Date of Leaving',
    'Wrong exit date in ECR / return',
    'Member still showing as active',
    'Exit correction pending with EPFO',
    'Company closed / no response',
    'EPFO technical / site error'
  ],
  eps_merging: [
    'DOB mismatch across EPS accounts',
    'EPS service not matching',
    'Scheme certificate already taken earlier',
    'Merge request pending with EPFO',
    'Name mismatch blocking merge',
    'EPFO technical / site error'
  ],
  advance_claims: [
    'Eligibility not met (service / purpose)',
    'Bank account / IFSC mismatch',
    'Required proof document type missing',
    'Form-31 under query / returned',
    'Claim pending with EPFO',
    'Member OTP / authentication failed',
    'EPFO technical / site error'
  ],
  kyc_issues: [
    'Aadhaar seeding failed / mismatch',
    'Bank KYC not approved',
    'PAN not linked',
    'Photo / signature pending',
    'Demographic data mismatch',
    'Pending with employer attestation',
    'EPFO technical / site error'
  ],
  e_nom: [
    'Family / nominee details incomplete',
    'Aadhaar of nominee not available',
    'e-Nomination pending digital approval',
    'Member OTP issue',
    'EPFO technical / site error'
  ],
  activation: [
    'UAN not activated',
    'Aadhaar not linked for activation',
    'Mobile not linked to UAN',
    'Password / OTP issue',
    'EPFO technical / site error'
  ],
  aadhar_linking: [
    'Aadhaar already linked to another UAN',
    'Demographic mismatch with UIDAI',
    'Bio-auth / OTP failure',
    'Pending with EPFO',
    'EPFO technical / site error'
  ],
  pf_final_settlement: [
    'Transfers still incomplete',
    'Form-19 under query / returned',
    'Bank IFSC / account mismatch',
    'Service eligibility issue',
    'Non-contributory period issue',
    'Pending with EPFO settlement',
    'Member authentication failed',
    'EPFO technical / site error'
  ],
  scheme_certificate: [
    'Form-10C under query / returned',
    'Already claimed / certificate exists',
    'DOB / name mismatch',
    'EPS eligibility issue',
    'Pending with EPFO',
    'EPFO technical / site error'
  ],
  pension_claim: [
    'Form-10D under query',
    'Age / service eligibility issue',
    'Bank details mismatch',
    'Pending with EPFO pension cell',
    'EPFO technical / site error'
  ],
  manual_pension: [
    'Manual file pending at EPFO',
    'Documents returned for correction',
    'Age / eligibility issue',
    'Bank details mismatch',
    'Physical visit / attestation pending'
  ],
  death_claim: [
    'Death certificate / legal heir docs pending',
    'Nominee vs legal heir dispute',
    'Form pending with EPFO',
    'Employer attestation pending',
    'Bank details of claimant mismatch'
  ],
  contribution_adjustment: [
    'Wrong wages / contribution in ECR',
    'Employer not filing correction',
    'EPFO not approving adjustment',
    'Challan / payment mismatch'
  ],
  dilink: [
    'Aadhaar–UAN delink pending EPFO',
    'Wrong linking with another UAN',
    'Member authentication failed'
  ],
  objections: [
    'Objection raised by EPFO',
    'Employer reply pending',
    'Member document reply pending',
    'Re-submission not done'
  ],
  physical_visit: [
    'Appointment / visit not done',
    'Officer asked for more docs',
    'File not traceable at office',
    'Waiting for next visit date'
  ]
};

const DEFAULT_OBSTACLES = [
  'Pending with employer',
  'Pending with EPFO',
  'Member action pending',
  'Document / data mismatch',
  'EPFO technical / site error'
];

const obstaclesFor = (factorKey) => OBSTACLE_BY_FACTOR[factorKey] || DEFAULT_OBSTACLES;

const GENERAL_QUESTIONS_BASE = [
  {
    id: 'needs_followup',
    label: 'Does this case need follow-up right now?',
    options: ['Yes', 'No'],
    stopIf: ['No']
  },
  {
    id: 'uan_login_ok',
    label: 'Can we log in to member UAN / Umang / member portal?',
    options: ['Yes', 'No', 'Password reset needed']
  },
  {
    id: 'aadhaar_seeded',
    label: 'Is Aadhaar seeded / linked on the present UAN?',
    options: ['Yes', 'No', 'Failed / mismatch', 'Not checked']
  },
  {
    id: 'bank_kyc_ok',
    label: 'Is bank account KYC approved on UAN?',
    options: ['Yes', 'No', 'Pending', 'Not checked']
  },
  {
    id: 'main_blocker',
    label: 'What is the main case blocker today?',
    options: [
      'No major blocker — moving',
      'Member not responding',
      'Employer / DSC pending',
      'EPFO processing delay',
      'KYC / Aadhaar / bank issue',
      'Transfer(s) incomplete',
      'Claim under query / returned',
      'Document mismatch (name/DOB/ID)',
      'Fee advance not received'
    ],
    stopIf: ['No major blocker — moving']
  },
  {
    id: 'case_age_days',
    label: 'How long is this case open?',
    options: ['0–7 days', '8–15 days', '16–30 days', '31–60 days', 'More than 60 days']
  },
  {
    id: 'advance_received',
    label: 'Is client fee advance received?',
    options: ['Yes', 'No', 'Partial']
  },
  {
    id: 'urgency',
    label: 'Case urgency?',
    options: ['Normal', 'Urgent', 'Very urgent']
  }
];

export const itemServiceTitle = (item) => {
  if (!item) return 'PF service';
  return item.instanceLabel ? `${item.label} — ${item.instanceLabel}` : item.label;
};

export const serviceEscalateName = (item, answers = {}) =>
  yn(answers.pf_service) || itemServiceTitle(item);

export const getGeneralQuestions = (pendingItems = []) => {
  const questions = [...GENERAL_QUESTIONS_BASE];
  const serviceOptions = [
    ...new Set(pendingItems.map((item) => itemServiceTitle(item)).filter(Boolean))
  ];
  if (serviceOptions.length > 0) {
    questions.splice(1, 0, {
      id: 'focus_service',
      label: 'Which pending PF service is the main issue for this brief?',
      options: [
        ...serviceOptions,
        ...(serviceOptions.length > 1 ? ['More than one pending service'] : [])
      ]
    });
  }
  return questions;
};

export const GENERAL_QUESTIONS = GENERAL_QUESTIONS_BASE;

const itemQuestionChain = (factorKey, item) => {
  const serviceLabel = item
    ? itemServiceTitle(item)
    : (CASE_FACTORS.find((f) => f.key === factorKey)?.label || 'This PF service');
  return [
    {
      id: 'pf_service',
      label: 'Which PF service is this issue about?',
      options: [serviceLabel]
    },
    {
      id: 'blocked',
      label: 'Is this service blocked right now?',
      options: ['Yes', 'No'],
      stopIf: ['No']
    },
    {
      id: 'obstacle',
      label: 'What is the real PF obstacle on this service?',
      options: obstaclesFor(factorKey)
    },
    {
      id: 'waiting_for',
      label: 'Who must act next on this service?',
      options: [
        'Paytiemp (us)',
        'Member',
        'Employer / HR / DSC',
        'EPFO',
        'Bank',
        'Not sure'
      ]
    },
    {
      id: 'pending_days',
      label: 'How long is this service pending?',
      options: ['0–7 days', '8–15 days', '16–30 days', '31–60 days', 'More than 60 days']
    },
    {
      id: 'claim_stage',
      label: 'Where is this service request now?',
      options: [
        'Not filed yet',
        'Filed — under process',
        'Under query / FOC',
        'Returned / rejected',
        'Approved — payment pending',
        'Not applicable'
      ]
    },
    {
      id: 'reminders',
      label: 'How many follow-ups already done on this service?',
      options: ['0', '1', '2', '3 or more']
    },
    {
      id: 'need_escalate',
      label: 'Do we need an official escalate letter for this service now?',
      options: ['Yes', 'No'],
      stopIf: ['No']
    },
    {
      id: 'escalate_to',
      label: 'Send escalate letter to whom?',
      options: [
        'Employer HR / PF desk',
        'Establishment DSC holder',
        'EPFO Regional Office',
        'EPFO field office',
        'EPFO online grievance / helpdesk'
      ]
    }
  ];
};

export const getItemQuestions = (factorKey, item) => itemQuestionChain(factorKey, item);

export const ITEM_QUESTIONS = itemQuestionChain('transfers');

export const getVisibleQuestions = (questions, answers = {}) => {
  const visible = [];
  for (const q of questions) {
    visible.push(q);
    const val = yn(answers[q.id]);
    if (val && Array.isArray(q.stopIf) && q.stopIf.includes(val)) {
      break;
    }
    if (!val) break;
  }
  return visible;
};

export const pruneAnswersToVisible = (questions, answers = {}) => {
  const visibleIds = new Set(getVisibleQuestions(questions, answers).map((q) => q.id));
  const next = { ...answers };
  Object.keys(next).forEach((id) => {
    if (!visibleIds.has(id)) next[id] = '';
  });
  return next;
};

export const countUnanswered = (questions, answers = {}) =>
  getVisibleQuestions(questions, answers).filter((q) => !yn(answers[q.id])).length;

const emptyGeneralAnswers = () => ({
  needs_followup: '',
  focus_service: '',
  uan_login_ok: '',
  aadhaar_seeded: '',
  bank_kyc_ok: '',
  main_blocker: '',
  case_age_days: '',
  advance_received: '',
  urgency: ''
});

const emptyItemAnswers = (factorKey, item) =>
  Object.fromEntries(getItemQuestions(factorKey, item).map((q) => [q.id, '']));

const isCompletedStatus = (status) =>
  String(status || '').trim().toLowerCase() === 'completed';

/** Full in-scope roadmap (includes Completed) */
export const buildRoadmapItems = (claim) => {
  const pricing = parseJson(claim?.pricing_data, {}) || {};
  const factorValues = resolveClaimFactorValues(claim || {});
  const active = getActiveCaseFactors(factorValues);
  const instancesMap = pricing.factorInstances || {};
  const items = [];

  active.forEach((factor) => {
    const field = FACTOR_STATUS_FIELD[factor.key];
    const status = (field && claim?.[field]) || 'Not Started';
    const instances = INSTANCE_TRACKED_FACTOR_KEYS.includes(factor.key)
      ? (instancesMap[factor.key] || [])
      : [];

    if (instances.length >= 1) {
      instances.forEach((inst) => {
        items.push({
          id: `${factor.key}::${inst.key}`,
          factorKey: factor.key,
          label: factor.label,
          instanceLabel: inst.label || inst.company_name || inst.member_id || 'Instance',
          company: inst.company_name || '',
          memberId: inst.member_id || '',
          status: inst.status || status
        });
      });
    } else {
      items.push({
        id: factor.key,
        factorKey: factor.key,
        label: factor.label,
        instanceLabel: null,
        company: '',
        memberId: '',
        status
      });
    }
  });

  return items;
};

/** Pending only (for questionnaire + drafts) */
export const buildBriefItems = (claim) =>
  buildRoadmapItems(claim).filter((item) => !isCompletedStatus(item.status));

export const loadCaseBrief = (claim) => {
  const pricing = parseJson(claim?.pricing_data, {}) || {};
  const saved = pricing.caseBrief || {};
  const items = buildBriefItems(claim);
  const roadmapItems = buildRoadmapItems(claim);
  const generalQuestions = getGeneralQuestions(items);
  const itemAnswers = {};
  items.forEach((item) => {
    itemAnswers[item.id] = {
      ...emptyItemAnswers(item.factorKey, item),
      ...(saved.itemAnswers?.[item.id] || {}),
      pf_service: itemServiceTitle(item)
    };
  });
  return {
    general: pruneAnswersToVisible(generalQuestions, {
      ...emptyGeneralAnswers(),
      ...(saved.general || {})
    }),
    itemAnswers,
    items,
    roadmapItems,
    updatedAt: saved.updatedAt || null
  };
};

/** Roadmap summary; escalate drafts listed only for pending services */
export const buildCaseSummaryText = ({ member, claim, general, items, itemAnswers, roadmapItems }) => {
  const name = member?.name || 'Member';
  const uan = member?.uan || '—';
  const overall = claim?.final_status || claim?.status || 'Not Started';
  const roadmap = roadmapItems?.length ? roadmapItems : items;
  const lines = [];

  lines.push('CASE ROADMAP');
  lines.push(`Member: ${name}`);
  lines.push(`UAN: ${uan}`);
  lines.push(`Overall status: ${overall}`);
  if (yn(general.focus_service)) {
    lines.push(`Main service in focus: ${yn(general.focus_service)}`);
  }
  lines.push('');
  lines.push('Roadmap (all in-scope services):');

  if (!roadmap.length) {
    lines.push('1. No case factors with count > 0.');
  } else {
    roadmap.forEach((item, index) => {
      const step = index + 1;
      const title = itemServiceTitle(item);
      const done = isCompletedStatus(item.status);
      const a = itemAnswers[item.id] || {};
      lines.push('');
      lines.push(`${step}. ${title}`);
      lines.push(`   Status: ${item.status}`);
      if (done) {
        lines.push('   Result: Completed — no questionnaire / no escalate draft.');
        return;
      }
      if (yn(a.blocked) === 'No') {
        lines.push('   Result: Open but not blocked.');
        return;
      }
      if (yn(a.blocked) === 'Yes') {
        lines.push(`   Service confirmed: ${serviceEscalateName(item, a)}`);
        lines.push(`   Obstacle: ${yn(a.obstacle) || '—'}`);
        lines.push(`   Waiting for: ${yn(a.waiting_for) || '—'}`);
        lines.push(`   Pending for: ${yn(a.pending_days) || '—'}`);
        lines.push(`   Stage: ${yn(a.claim_stage) || '—'}`);
        lines.push(`   Follow-ups: ${yn(a.reminders) || '—'}`);
        lines.push(
          yn(a.need_escalate) === 'Yes'
            ? `   Escalate draft: Yes → ${yn(a.escalate_to) || 'authority not set'}`
            : `   Escalate draft: ${yn(a.need_escalate) || 'Not answered'}`
        );
      } else {
        lines.push('   Result: Pending answers in questionnaire.');
      }
    });
  }

  const pendingDrafts = items.filter((item) => yn(itemAnswers[item.id]?.need_escalate) === 'Yes');
  lines.push('');
  lines.push('Escalate drafts (pending services only):');
  if (!pendingDrafts.length) {
    lines.push('• None yet — mark a pending service blocked + escalate = Yes.');
  } else {
    pendingDrafts.forEach((item) => {
      const a = itemAnswers[item.id] || {};
      lines.push(`• ${serviceEscalateName(item, a)} → ${yn(a.escalate_to) || 'authority not set'}`);
    });
  }

  return lines.join('\n');
};

const factorPurposeLine = (factorKey, label) => {
  const map = {
    transfers: 'PF transfer (Form-13 / previous account transfer)',
    jd_issues: 'Joint Declaration / JD correction',
    exit_date: 'Exit date (Date of Leaving) correction',
    advance_claims: 'PF advance claim (Form-31)',
    eps_merging: 'EPS merging',
    kyc_issues: 'KYC updation',
    e_nom: 'e-Nomination',
    pf_final_settlement: 'PF final settlement (Form-19)',
    scheme_certificate: 'Scheme certificate (Form-10C)',
    pension_claim: 'Pension claim (Form-10D)',
    manual_pension: 'Monthly Pension Application',
    activation: 'UAN / PF activation',
    aadhar_linking: 'Aadhaar linking',
    death_claim: 'EDLI Claim',
    contribution_adjustment: 'Contribution adjustment',
    dilink: 'Aadhaar–UAN delink',
    objections: 'EPFO objection clearance',
    physical_visit: 'Physical visit to PF office'
  };
  return map[factorKey] || String(label || 'PF service');
};

/** Point-wise official draft — no internal claim ID */
export const buildEscalationDraft = ({ member, item, answers, general }) => {
  const name = member?.name || 'the member';
  const uan = member?.uan || '__________';
  const to = yn(answers.escalate_to) || 'Concerned Authority';
  const waiting = yn(answers.waiting_for) || 'concerned office';
  const days = yn(answers.pending_days) || 'several days';
  const reminders = yn(answers.reminders) || '0';
  const obstacle = yn(answers.obstacle) || 'processing delay';
  const stage = yn(answers.claim_stage) || 'under process';
  const service = serviceEscalateName(item, answers);
  const purpose = factorPurposeLine(item.factorKey, item.label);
  const urgency = yn(general.urgency) || 'Normal';
  const focus = yn(general.focus_service);

  const points = [
    `Service to escalate: ${service}`,
    `Nature of work: ${purpose}`
  ];
  if (item.company) points.push(`Establishment / company: ${item.company}`);
  if (item.memberId) points.push(`Previous Member ID: ${item.memberId}`);
  points.push(`Present status: ${item.status}`);
  points.push(`Request stage: ${stage}`);
  points.push(`Reported obstacle: ${obstacle}`);
  points.push(`Pending for: ${days}`);
  points.push(`Next action expected from: ${waiting}`);
  points.push(`Follow-ups already done: ${reminders}`);
  points.push(`Urgency: ${urgency}`);
  if (focus && focus !== 'More than one pending service') {
    points.push(`Case main focus service: ${focus}`);
  }

  return [
    `To,`,
    `${to}`,
    ``,
    `Subject: Request for early action on ${service} — UAN ${uan} — ${name}`,
    ``,
    `Respected Sir/Madam,`,
    ``,
    `We are writing on behalf of ${name}, UAN ${uan}.`,
    ``,
    `We request your kind attention to the following points regarding the pending PF service:`,
    ``,
    ...points.map((p) => `• ${p}`),
    ``,
    `Kindly examine this matter for the service mentioned above and take necessary action at the earliest so that the member’s PF claim can proceed without further delay.`,
    ``,
    `Thanking you,`,
    `Yours faithfully,`,
    `PF Claims Management Team`,
    `Paytiemp`,
    ``,
    `(Auto-draft — review before sending)`
  ].join('\n');
};

export const buildAllEscalationDrafts = ({ member, claim, general, items, itemAnswers }) =>
  items
    .filter((item) => yn(itemAnswers[item.id]?.need_escalate) === 'Yes')
    .map((item) => ({
      id: item.id,
      title: serviceEscalateName(item, itemAnswers[item.id] || {}),
      to: yn(itemAnswers[item.id]?.escalate_to) || 'Not set',
      draft: buildEscalationDraft({
        member,
        claim,
        item,
        answers: itemAnswers[item.id] || {},
        general
      })
    }));

export const factorLabelByKey = (key) =>
  CASE_FACTORS.find((f) => f.key === key)?.label || key;
